<!--
  ~ Copyright (c) 2023 D2ArmorPicker by Mijago.
  ~
  ~ This program is free software: you can redistribute it and/or modify
  ~ it under the terms of the GNU Affero General Public License as published
  ~ by the Free Software Foundation, either version 3 of the License, or
  ~ (at your option) any later version.
  ~
  ~ This program is distributed in the hope that it will be useful,
  ~ but WITHOUT ANY WARRANTY; without even the implied warranty of
  ~ MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  ~ GNU Affero General Public License for more details.
  ~
  ~ You should have received a copy of the GNU Affero General Public License
  ~ along with this program.  If not, see <http://www.gnu.org/licenses/>.
  -->

<div>
  This table shows you the expected stats of any item:
  <table class="specificStatTable">
    <thead>
      <tr>
        <th>Name</th>
        <th class="ability-column">{{ ArmorStatNames[ArmorStat.StatHealth] }}</th>
        <th class="ability-column">{{ ArmorStatNames[ArmorStat.StatMelee] }}</th>
        <th class="ability-column">{{ ArmorStatNames[ArmorStat.StatGrenade] }}</th>
        <th class="ability-column">{{ ArmorStatNames[ArmorStat.StatSuper] }}</th>
        <th class="ability-column">{{ ArmorStatNames[ArmorStat.StatClass] }}</th>
        <th class="ability-column">{{ ArmorStatNames[ArmorStat.StatWeapon] }}</th>
        <th></th>
        <th></th>
        <th>MW</th>
        <th></th>
      </tr>
    </thead>
    <!-- Items -->
    <ng-container *ngFor="let i of ArmorItems">
      <tr>
        <td>
          <u
            #tooltip="matTooltip"
            *ngIf="i.masterworked"
            class="item-name-mw"
            matTooltip="This item is already masterworked."
            >{{ i.name }}</u
          >
          <span *ngIf="!i.masterworked">{{ i.name }}</span>
        </td>
        <ng-container *ngVar="getMasterworkBonus(i) as bonus">
          <td class="item-stat-container">
            <span [class.text-mw]="i.masterworked" class="item-stat-value">{{
              i.stats[ArmorStat.StatHealth] + bonus[1]
            }}</span>
          </td>
          <td class="item-stat-container">
            <span [class.text-mw]="i.masterworked" class="item-stat-value">{{
              i.stats[ArmorStat.StatMelee] + bonus[5]
            }}</span>
          </td>
          <td class="item-stat-container">
            <span [class.text-mw]="i.masterworked" class="item-stat-value">{{
              i.stats[ArmorStat.StatGrenade] + bonus[3]
            }}</span>
          </td>
          <td class="item-stat-container">
            <span [class.text-mw]="i.masterworked" class="item-stat-value">{{
              i.stats[ArmorStat.StatSuper] + bonus[4]
            }}</span>
          </td>
          <td class="item-stat-container">
            <span [class.text-mw]="i.masterworked" class="item-stat-value">{{
              i.stats[ArmorStat.StatClass] + bonus[2]
            }}</span>
          </td>
          <td class="item-stat-container">
            <span [class.text-mw]="i.masterworked" class="item-stat-value">{{
              i.stats[ArmorStat.StatWeapon] + bonus[0]
            }}</span>
          </td>
        </ng-container>
        <td class="icon-column">
          <!-- Item Icon -->
          <div [itemTooltip]="i" class="item-icon-container">
            <app-item-icon
              [itemHash]="i.hash"
              [masterworked]="i.masterworked"
              [source]="i.source"
              class="item-icon">
            </app-item-icon>
          </div>
        </td>
        <td class="icon-column">
          <div style="position: relative; width: 25px">
            <app-armor-perk-icon [perk]="i.perk || 0"></app-armor-perk-icon>
          </div>
        </td>
        <td style="text-align: center">
          <ng-container *ngIf="i.armorSystem === 2; else showLevel">
            <mat-icon aria-label="Masterworked" *ngIf="i.masterworked">check_circle</mat-icon>
          </ng-container>
          <ng-template #showLevel>
            <span *ngIf="i.masterworkLevel !== undefined">{{ i.masterworkLevel }}</span>
          </ng-template>
        </td>
        <!-- Disable -->
        <td class="icon-column">
          <button
            (click)="disableItem(i.itemInstanceId)"
            style="padding: 0; top: 0; left: 0"
            class="item-info-menu-btn"
            mat-icon-button
            matTooltip="Disable this item from the results. It will not be used to generate results anymore, but you can always undo this.">
            <mat-icon>block</mat-icon>
          </button>
        </td>

        <!-- Moving to inventory -->
        <td *ngIf="i.transferState === 1 || i.transferState === 2" class="icon-column">
          <mat-progress-spinner
            class="item-loading-spinner"
            color="primary"
            diameter="22"
            mode="indeterminate">
          </mat-progress-spinner>
        </td>
        <td *ngIf="i.transferState === 3">
          <mat-icon
            #tooltip="matTooltip"
            aria-hidden="false"
            aria-label="Success"
            class="item-moved-icon"
            inline
            matTooltip="Item successfully moved to inventory.">
            check_circle_outline
          </mat-icon>
        </td>
        <td *ngIf="i.transferState === 4">
          <mat-icon
            #tooltip="matTooltip"
            aria-hidden="false"
            aria-label="Error"
            class="report-problem-icon"
            inline
            matTooltip="This item could not be moved. Make sure that there is enough space on your character. This tool will not move items out of your inventory.">
            error_outline
          </mat-icon>
        </td>
        <!-- // Moving to inventory -->
      </tr>
    </ng-container>
    <!--Sum line without mods-->
    <tr *ngVar="element?.statsNoMods as stats" class="result-total-gear result-total highlight-row">
      <td>Total (armor)</td>
      <td *ngIf="!!stats">
        {{ stats[ArmorStat.StatHealth] }}
        <app-stat-icon [stat]="ArmorStat.StatHealth" class="statIcon"></app-stat-icon>
      </td>
      <td *ngIf="!!stats">
        {{ stats[ArmorStat.StatMelee] }}
        <app-stat-icon [stat]="ArmorStat.StatMelee" class="statIcon"></app-stat-icon>
      </td>
      <td *ngIf="!!stats">
        {{ stats[ArmorStat.StatGrenade] }}
        <app-stat-icon [stat]="ArmorStat.StatGrenade" class="statIcon"></app-stat-icon>
      </td>
      <td *ngIf="!!stats">
        {{ stats[ArmorStat.StatSuper] }}
        <app-stat-icon [stat]="ArmorStat.StatSuper" class="statIcon"></app-stat-icon>
      </td>
      <td *ngIf="!!stats">
        {{ stats[ArmorStat.StatClass] }}
        <app-stat-icon [stat]="ArmorStat.StatClass" class="statIcon"></app-stat-icon>
      </td>
      <td *ngIf="!!stats">
        {{ stats[ArmorStat.StatWeapon] }}
        <app-stat-icon [stat]="ArmorStat.StatWeapon" class="statIcon"></app-stat-icon>
      </td>
    </tr>
    <!-- Config Mods -->
    <tr class="result-config">
      <td>Configuration</td>
      <td
        ><span
          *ngIf="this.configValues[ArmorStat.StatHealth] !== 0"
          [class]="this.configValues[ArmorStat.StatHealth] > 0 ? 'positive' : 'negative'">
          <span *ngIf="this.configValues[ArmorStat.StatHealth] > 0">+</span
          >{{ this.configValues[ArmorStat.StatHealth] }}
        </span></td
      >
      <td
        ><span
          *ngIf="this.configValues[ArmorStat.StatMelee] !== 0"
          [class]="this.configValues[ArmorStat.StatMelee] > 0 ? 'positive' : 'negative'">
          <span *ngIf="this.configValues[ArmorStat.StatMelee] > 0">+</span
          >{{ this.configValues[ArmorStat.StatMelee] }}
        </span></td
      >
      <td
        ><span
          *ngIf="this.configValues[ArmorStat.StatGrenade] !== 0"
          [class]="this.configValues[ArmorStat.StatGrenade] > 0 ? 'positive' : 'negative'">
          <span *ngIf="this.configValues[ArmorStat.StatGrenade] > 0">+</span
          >{{ this.configValues[ArmorStat.StatGrenade] }}
        </span></td
      >
      <td
        ><span
          *ngIf="this.configValues[ArmorStat.StatSuper] !== 0"
          [class]="this.configValues[ArmorStat.StatSuper] > 0 ? 'positive' : 'negative'">
          <span *ngIf="this.configValues[ArmorStat.StatSuper] > 0">+</span
          >{{ this.configValues[ArmorStat.StatSuper] }}
        </span></td
      >
      <td
        ><span
          *ngIf="this.configValues[ArmorStat.StatClass] !== 0"
          [class]="this.configValues[ArmorStat.StatClass] > 0 ? 'positive' : 'negative'">
          <span *ngIf="this.configValues[ArmorStat.StatClass] > 0">+</span
          >{{ this.configValues[ArmorStat.StatClass] }}
        </span></td
      >
      <td
        ><span
          *ngIf="this.configValues[ArmorStat.StatWeapon] !== 0"
          [class]="this.configValues[ArmorStat.StatWeapon] > 0 ? 'positive' : 'negative'">
          <span *ngIf="this.configValues[ArmorStat.StatWeapon] > 0">+</span
          >{{ this.configValues[ArmorStat.StatWeapon] }}
        </span></td
      >
    </tr>
    <!-- +5 Mods -->
    <tr *ngVar="element?.mods as mods" class="highlight-row">
      <td>Minor Mods</td>
      <td *ngVar="mods | count: StatModifier.MINOR_HEALTH as count">
        <span *ngIf="count > 0" class="positive">{{ count }}×5</span>
      </td>
      <td *ngVar="mods | count: StatModifier.MINOR_MELEE as count">
        <span *ngIf="count > 0" class="positive">{{ count }}×5</span>
      </td>
      <td *ngVar="mods | count: StatModifier.MINOR_GRENADE as count">
        <span *ngIf="count > 0" class="positive">{{ count }}×5</span>
      </td>
      <td *ngVar="mods | count: StatModifier.MINOR_SUPER as count">
        <span *ngIf="count > 0" class="positive">{{ count }}×5</span>
      </td>
      <td *ngVar="mods | count: StatModifier.MINOR_CLASS as count">
        <span *ngIf="count > 0" class="positive">{{ count }}×5</span>
      </td>
      <td *ngVar="mods | count: StatModifier.MINOR_WEAPON as count">
        <span *ngIf="count > 0" class="positive">{{ count }}×5</span>
      </td>
    </tr>
    <!-- +10 Mods -->
    <tr *ngVar="element?.mods as mods">
      <td>Major Mods</td>
      <td *ngVar="mods | count: StatModifier.MAJOR_HEALTH as count">
        <span *ngIf="count > 0" class="positive">{{ count }}×10</span>
      </td>
      <td *ngVar="mods | count: StatModifier.MAJOR_MELEE as count">
        <span *ngIf="count > 0" class="positive">{{ count }}×10</span>
      </td>
      <td *ngVar="mods | count: StatModifier.MAJOR_GRENADE as count">
        <span *ngIf="count > 0" class="positive">{{ count }}×10</span>
      </td>
      <td *ngVar="mods | count: StatModifier.MAJOR_SUPER as count">
        <span *ngIf="count > 0" class="positive">{{ count }}×10</span>
      </td>
      <td *ngVar="mods | count: StatModifier.MAJOR_CLASS as count">
        <span *ngIf="count > 0" class="positive">{{ count }}×10</span>
      </td>
      <td *ngVar="mods | count: StatModifier.MAJOR_WEAPON as count">
        <span *ngIf="count > 0" class="positive">{{ count }}×10</span>
      </td>
    </tr>
    <ng-container *ngVar="element?.artifice as mods">
      <tr>
        <td>Artifice Mods</td>
        <td *ngVar="mods | count: StatModifier.ARTIFICE_HEALTH as count">
          <span *ngIf="count > 0" class="positive">{{ count }}×3</span>
        </td>
        <td *ngVar="mods | count: StatModifier.ARTIFICE_MELEE as count">
          <span *ngIf="count > 0" class="positive">{{ count }}×3</span>
        </td>
        <td *ngVar="mods | count: StatModifier.ARTIFICE_GRENADE as count">
          <span *ngIf="count > 0" class="positive">{{ count }}×3</span>
        </td>
        <td *ngVar="mods | count: StatModifier.ARTIFICE_SUPER as count">
          <span *ngIf="count > 0" class="positive">{{ count }}×3</span>
        </td>
        <td *ngVar="mods | count: StatModifier.ARTIFICE_CLASS as count">
          <span *ngIf="count > 0" class="positive">{{ count }}×3</span>
        </td>
        <td *ngVar="mods | count: StatModifier.ARTIFICE_WEAPON as count">
          <span *ngIf="count > 0" class="positive">{{ count }}×3</span>
        </td>
      </tr>
    </ng-container>

    <!-- Final sum line -->
    <tr *ngVar="element?.stats as stats" class="result-total-all result-total highlight-row">
      <td>Total</td>
      <td>
        {{ stats[ArmorStat.StatHealth] }}
        <app-stat-icon [stat]="ArmorStat.StatHealth" class="statIcon"></app-stat-icon>
      </td>
      <td>
        {{ stats[ArmorStat.StatMelee] }}
        <app-stat-icon [stat]="ArmorStat.StatMelee" class="statIcon"></app-stat-icon>
      </td>
      <td>
        {{ stats[ArmorStat.StatGrenade] }}
        <app-stat-icon [stat]="ArmorStat.StatGrenade" class="statIcon"></app-stat-icon>
      </td>
      <td>
        {{ stats[ArmorStat.StatSuper] }}
        <app-stat-icon [stat]="ArmorStat.StatSuper" class="statIcon"></app-stat-icon>
      </td>
      <td>
        {{ stats[ArmorStat.StatClass] }}
        <app-stat-icon [stat]="ArmorStat.StatClass" class="statIcon"></app-stat-icon>
      </td>
      <td>
        {{ stats[ArmorStat.StatWeapon] }}
        <app-stat-icon [stat]="ArmorStat.StatWeapon" class="statIcon"></app-stat-icon>
      </td>
    </tr>
  </table>
</div>
<ng-container *ngVar="calculateRequiredMasterworkCost() as cost">
  <div *ngIf="cost['total'] > 0" style="margin-top: 10px">
    Total material cost:
    <span *ngIf="cost['glimmer'] > 0" class="currency-icon-container" matTooltip="Glimmer">
      <img
        alt="Glimmer"
        class="currency-icon"
        src="https://www.bungie.net/common/destiny2_content/icons/88993eaeedad32f42072df6f77f47311.png" />
      {{ cost["glimmer"] | number }}
    </span>

    <span *ngIf="cost['core'] > 0" class="currency-icon-container" matTooltip="Enhancement Cores">
      <img
        alt="Enhancement Cores"
        class="currency-icon"
        src="https://www.bungie.net/common/destiny2_content/icons/0a4eb695ed55b4e720ddc401460cc0f9.jpg" />
      {{ cost["core"] }}
    </span>

    <span *ngIf="cost['prism'] > 0" class="currency-icon-container" matTooltip="Enhancement Prism">
      <img
        alt="Enhancement Prism"
        class="currency-icon"
        src="https://www.bungie.net/common/destiny2_content/icons/dea2a35badf7466d4c2c2697ce6e8d87.jpg" />
      {{ cost["prism"] }}
    </span>

    <span *ngIf="cost['ascshard'] > 0" class="currency-icon-container" matTooltip="Ascendant Shard">
      <img
        alt="Ascendant Shard"
        class="currency-icon"
        src="https://www.bungie.net/common/destiny2_content/icons/0271d214fc3ec91b3def799a4b286b46.jpg" />
      {{ cost["ascshard"] }}
    </span>
  </div>
</ng-container>

<div style="display: flex; align-items: center; flex-direction: column">
  <div class="buttonContainer">
    <button
      (click)="CopyDIMQuery()"
      color="primary"
      mat-raised-button
      matTooltip="Use this button to copy a DIM search query to your clip board. It allows you to search the given items in DIM.">
      Copy DIM query to clipboard
    </button>
    <button
      (click)="goToDIM()"
      color="primary"
      mat-raised-button
      matTooltip="Use this button to open this build as a DIM Loadout.">
      Open Loadout in DIM
    </button>
    <br />
    <small>The following two buttons may not work for some at the moment:</small><br />
    <button
      (click)="moveItems()"
      mat-stroked-button
      matTooltip="Click this button to move Items to your inventory. Make sure that you have enough space in your inventory. This tool will not move any items out of your inventory.">
      Move items to inventory (beta)
    </button>
    <button
      (click)="moveItems(true)"
      mat-stroked-button
      matTooltip="Click this button to equip the Items. Make sure that you have enough space in your inventory. This tool will not move any items out of your inventory.">
      Equip Items (beta)
    </button>
  </div>
</div>

<br />
<br />
